import { useEffect, useMemo, useState } from "react";
import { CheckCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { FALLBACK_PLANS, FIRMA_CONTABLE, loadPublicPlans, quoteVolume, type PublicPlan } from "@/lib/plans";

function formatPrice(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

export default function PricingSection() {
  const [volume, setVolume] = useState(500);
  const [plans, setPlans] = useState<PublicPlan[]>(FALLBACK_PLANS);

  useEffect(() => {
    let cancelled = false;
    loadPublicPlans().then((rows) => {
      if (!cancelled) setPlans(rows);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const quote = useMemo(() => quoteVolume(plans, volume), [plans, volume]);
  const topPlan = plans.reduce<PublicPlan | null>(
    (best, plan) => (!best || plan.included_ecf > best.included_ecf ? plan : best),
    null,
  );
  const selected = quote.selected;
  const overageRef = topPlan || selected;

  return (
    <section id="precios" className="bg-slate-50 py-24 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-flex items-center px-3 py-1 rounded-full border border-slate-200 text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">
            Precios
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
            Planes a la medida de tu crecimiento
          </h2>
          <p className="text-slate-500 text-base">
            Todos los planes incluyen Odoo ERP preconfigurado, firma digital de e-CF y soporte técnico.
          </p>
        </div>

        <div className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 max-w-4xl mx-auto mb-16 border border-slate-800 shadow-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                <span className="text-blue-400">📊</span> Calculador de Costo Mensual
              </h3>
              <p className="text-slate-400 text-xs mb-6">
                Arrastra el control o ingresa tu volumen estimado para ver qué plan te corresponde y los posibles cargos por excedentes.
              </p>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-slate-300">Volumen mensual estimado:</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={volume}
                      onChange={(e) => setVolume(Math.max(0, Math.min(100000, Number(e.target.value))))}
                      className="bg-slate-800 border border-slate-700 text-white text-center font-bold px-3 py-1 rounded w-24 text-xs focus:outline-none focus:border-blue-500"
                    />
                    <span className="text-xs text-slate-500">e-CF</span>
                  </div>
                </div>

                <input
                  type="range"
                  min="50"
                  max="3000"
                  step="50"
                  value={volume > 3000 ? 3000 : volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />

                <div className="flex justify-between text-[10px] text-slate-500">
                  <span>50 e-CF</span>
                  {plans[1] ? <span>{plans[1].included_ecf.toLocaleString()} e-CF</span> : <span />}
                  {topPlan ? <span>{topPlan.included_ecf.toLocaleString()} e-CF</span> : <span />}
                  <span>3,000+ e-CF</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-750 rounded-xl p-6 flex flex-col justify-between h-full">
              <div className="space-y-4">
                <div className="flex justify-between border-b border-slate-700 pb-3 text-xs">
                  <span className="text-slate-400">Plan recomendado:</span>
                  <span className="text-blue-400 font-bold">{selected?.name || "—"}</span>
                </div>
                <div className="flex justify-between items-end">
                  <span className="text-slate-400 text-xs mb-1">Costo Mensual Estimado:</span>
                  <div className="text-right">
                    <span className="text-[10px] text-slate-400 block">{selected?.currency || "USD"}</span>
                    <span className="text-3xl font-black text-white">${quote.total.toFixed(2)}</span>
                    <span className="text-[10px] text-slate-500 block">/mes estimado</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 border-t border-slate-700 pt-4 text-[10px] text-slate-400">
                {selected && volume <= selected.included_ecf ? (
                  <span className="text-emerald-400 font-medium">
                    ✓ Volumen totalmente cubierto por la cuota del {selected.name} ({selected.included_ecf} e-CF incluidos).
                  </span>
                ) : selected ? (
                  <span>
                    Base del {selected.name} (${formatPrice(selected.price_monthly)}) + ${quote.extraCost} USD por {quote.extraBlocks} bloque(s) excedente(s) de {selected.overage_block_size} e-CF.
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mb-12 -mt-10">
          * Para volúmenes superiores a {topPlan ? topPlan.included_ecf.toLocaleString() : "2,000"} comprobantes electrónicos (e-CF) mensuales o integraciones avanzadas, por favor <Link to="/contacto" className="text-blue-800 dark:text-blue-400 font-semibold hover:underline">contacta a nuestro equipo de ventas</Link> para recibir una propuesta a medida.
        </p>

        <div className={`grid grid-cols-1 ${plans.length === 2 ? "md:grid-cols-2 max-w-4xl" : "md:grid-cols-3"} gap-6 mx-auto`}>
          {plans.map((plan) => (
            <div
              key={plan.code}
              className={`relative bg-white rounded-2xl p-8 border transition-all duration-200 group hover:-translate-y-1 ${
                plan.popular
                  ? "border-blue-800 shadow-lg shadow-blue-900/10"
                  : "border-slate-100 hover:border-blue-200"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-4 py-1 rounded-full border border-emerald-200">
                    Más popular
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-lg font-bold text-slate-900 mb-1">{plan.name}</h3>
                <p className="text-sm text-slate-500 mb-4">{plan.description}</p>
                <div className="flex items-end gap-1">
                  <span className="text-sm text-slate-500 mb-1">{plan.currency}</span>
                  <span className="font-black text-slate-900 text-4xl">
                    {formatPrice(plan.price_monthly)}
                  </span>
                  <span className="text-sm text-slate-400 mb-1">{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <CheckCircle size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              <Link
                to="/signup"
                className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  plan.popular
                    ? "bg-blue-800 text-white hover:bg-blue-700"
                    : "border border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white"
                }`}
              >
                {plan.cta || "Comenzar"}
                <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/contadores"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-800 text-sm font-semibold text-blue-800 hover:bg-blue-800 hover:text-white transition-colors"
          >
            {FIRMA_CONTABLE.badge}
            <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-20 pt-16 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <h4 className="text-sm font-bold text-slate-900 mb-2">📁 Tus datos son tuyos</h4>
            <p className="text-xs text-slate-500 leading-relaxed">
              Si un día decides irte, te entregamos el respaldo completo de tu base de datos. Sin secuestros de información.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-900 mb-2">⚡ Nunca bloqueamos tu facturación</h4>
            <p className="text-xs text-slate-500 leading-relaxed">
              Si superas tu plan, sigues emitiendo con normalidad. Los excedentes de e-CF se facturan en tu siguiente ciclo.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-900 mb-2">🛡️ Contingencia documentada</h4>
            <p className="text-xs text-slate-500 leading-relaxed">
              Si la DGII presenta indisponibilidad, tu negocio sigue vendiendo bajo el procedimiento de contingencia previsto en la Ley 32-23.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-900 mb-2">🤝 Acompañamiento hasta la autorización</h4>
            <p className="text-xs text-slate-500 leading-relaxed">
              Te guiamos paso a paso en el set de pruebas oficiales de la DGII hasta que quedes autorizado como emisor electrónico.
            </p>
          </div>
        </div>

        <p className="text-center text-sm text-slate-400 mt-12">
          Todos los precios en {overageRef?.currency || "USD"}. ITBIS no incluido. Los excedentes de facturas se cobran por bloques adicionales de {overageRef?.overage_block_size || 100} e-CF a {overageRef?.currency || "USD"}$ {formatPrice(overageRef?.overage_block_price || 12)}.
        </p>
      </div>
    </section>
  );
}
